import { ReadOnlyText } from "@components/inputs/utils";
import Skeleton from "@components/skeleton/Skeleton";
import { isObjectEmpty } from "@utils/general";
import autosize from "autosize";
import { useComponentDidMount } from "hooks/useComponentDidMount";
import { useComponentDidUpdate } from "hooks/useComponentDidUpdate";
import { useComponentWillUnmount } from "hooks/useComponentWillUnmount";
import React, { FocusEvent, useRef } from "react";

import { WithDomManipulator, withDomManipulator } from "../../../contexts/domManipulator/withDomManipulator";
import { BasicInputSizes, TextAlign } from "../../../enums";
import { IAuditTrailData } from "../../../model/Model";
import { IValidationError } from "../../../model/Validator.types";
import { IDayInterval } from "../date/utils";
import { WithErrorAndTooltip, withErrorAndTooltip, WithErrorAndTooltipProps } from "../formError/WithErrorAndTooltip";
import { useInputEventHandlers } from "./hooks/useInputHandlers";
import { InputRenderer } from "./InputRenderer";
import { IFormatterFns, withFormatter } from "./WithFormatter";

export interface IInputOnChangeEvent<Type = string | number | Date | IDayInterval> {
    value: Type;
    origEvent?: React.ChangeEvent<HTMLInputElement>;
    /** Distinguish between liveChange (user is writing) and change that should be considered as trigger
     * - steppers in NumericInput, selection in date popups */
    triggerAdditionalTasks?: boolean;
}

export interface IInputOnBlurEvent {
    origEvent: FocusEvent<HTMLInputElement>;
    wasChanged: boolean;
}

export interface IInputRowsChangeEvent {
    numRows: number;
    newHeight: string;
}

/** Shared props for every component that lets user write or select some kind of value.
 * Each of those components has to be able to work with these props.*/
export interface IValueInputComponentProps {
    /** Component should render as always, but in disabled state (lowered opacity, no pointer events) */
    isDisabled?: boolean;
    /** Component should render as text only, without input visuals */
    isReadOnly?: boolean;
    isLight?: boolean;
    isSkeleton?: boolean;
    showChange?: boolean;
}

/** Shared props for every component that internaly renders Input */
export interface ISharedInputProps extends IValueInputComponentProps, IFormatterFns<any> {
    width?: string;
    height?: string;
    error?: IValidationError;
    /** Type of starting border-radius */
    isSharpLeft?: boolean;
    /** Type of ending border-radius */
    isSharpRight?: boolean;
    name?: string;
    id?: string;
    textAlign?: TextAlign;
    // text put into input and writelines as default(no value) background text
    placeholder?: string;
    // html attribute autocomplete, defaults to "off"
    autocomplete?: string;
    // text put into the end of the input and writeline, with grey color and always visible - typically to show unit
    unit?: string;
    // like placeholder, but doesn't disappear when value is used
    description?: string;
    tabularData?: string;
    isSkeleton?: boolean;
    // disables just the input value
    // TODO remove if we won't need this isDisabled is enough
    isSynchronized?: boolean;
    isMultiLine?: boolean;
    maxRows?: number;

    secondaryIcon?: React.ReactElement;

    isLoading?: boolean;
    debouncedWait?: number;
    /** Indicates that icon has different action then the input click*/
    iconHasDifferentAction?: boolean;
    /** Only used for multiline input.
     * Called with current height of the input, when number of rows is changed, because of the length of the value. */
    onRowsChange?: (args: IInputRowsChangeEvent) => void;

    auditTrailData?: IAuditTrailData;
}

export const SHARED_INPUT_PROPS = [
    "width",
    "height",
    "error",
    "isDisabled",
    "isReadOnly",
    "isLight",
    "id",
    "isLoading",
    "isSharpLeft",
    "isSharpRight",
    "isSynchronized",
    "name",
    "textAlign",
    "tooltip",
    "isRequired",
    "showChange",
    "placeholder",
    "isMultiLine",
    "maxRows",
    "onRowsChange",
    "auditTrailData",
    "debouncedWait",
    "iconHasDifferentAction",
    "isSkeleton",
];

export const getSharedInputProps = (props: ISharedInputProps) => {
    const sharedProps: any = {};

    for (const sharedProp of SHARED_INPUT_PROPS) {
        sharedProps[sharedProp] = props[sharedProp as keyof ISharedInputProps];
    }

    return sharedProps;
};

export interface IInputProps<Type = string> extends ISharedInputProps, WithErrorAndTooltip {
    onIconClick?: (e: React.MouseEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onChange?: (args: IInputOnChangeEvent<Type>) => void;
    onBlur?: (args: IInputOnBlurEvent) => void;
    onWheel?: (event: React.WheelEvent<HTMLElement>) => void;
    onClick?: (e: React.MouseEvent) => void;
    icon?: React.ReactElement;
    iconTitle?: string;
    /** Content added to the end of the input. Used f.e. in line items (add checkbox) */
    content?: React.ReactElement;
    /** Content rendered before the actual input */
    contentBefore?: React.ReactElement;
    link?: React.ReactElement;
    passRef?: React.Ref<HTMLInputElement>;
    value?: Type;
    type?: string;
    inputProps?: React.AllHTMLAttributes<HTMLInputElement>;
    outerRef?: React.RefObject<HTMLDivElement>;
    outerProps?: any;
    className?: string;
    isActive?: boolean;
    cursor?: string;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    selectOnFocus?: boolean;
    debouncedWait?: number;
}

export type IInputPropsExtended = IInputProps & WithDomManipulator;

export interface IDomProps {
    numRows: number;
    newHeight: number;
}

export interface InputComponentRef {
    _inputRef: React.RefObject<HTMLInputElement>;
    _wrapperRef: React.RefObject<HTMLElement>;
    // You might also expose methods here, e.g., focus: () => void;
}

const Input = React.forwardRef<InputComponentRef, IInputPropsExtended>(
    // The function receives 'props' and 'ref'
    ({ iconHasDifferentAction = false, ...props }: IInputPropsExtended, ref) => {
    const _inputRef = useRef<HTMLInputElement>(null);
    const _wrapperRef = useRef<HTMLElement>(null);
    const _wasChangedSinceBlur = useRef(false);


    // for real this is ugly, it would be much better to use some kind of state instead of exposing refs
    React.useImperativeHandle(ref, () => ({
        _inputRef: _inputRef,
        _wrapperRef: _wrapperRef,

    }));

    const handlers = useInputEventHandlers({
        inputProps: props,
        inputRef: _inputRef,
        wasChangedSinceBlurRef: _wasChangedSinceBlur,
        wrapperRef: _wrapperRef,

    });

    useComponentDidMount(() => {
        /** WARNING! changing multiLine during component lifecycle isn't supported */
        if (!props.isReadOnly && props.isMultiLine) {
            autosize(_inputRef.current);

            _inputRef.current.addEventListener("autosize:resized", handlers.handleAutosizeResize);

            if (props.value) {
                // force height event after first render, the input can already be higher than one row
                handlers.handleRowsChange(true);
            }
        }
    });

    useComponentWillUnmount(() => {
        if (props.isMultiLine) {
            //TODO: uncomment
            _inputRef?.current?.removeEventListener("autosize:resized", handlers.handleAutosizeResize);
        }
    });

    useComponentDidUpdate((prevProps) => {
        if (props.isMultiLine && prevProps.width !== props.width) {
            // autosize isn't triggered when value input is changed programatically
            // => Select calls autosize.update manually on item selected
            // prevProps.value !== props.value here would solve this (maybe not efficient?)
            autosize.update(_inputRef.current);
        }
        if (props.value !== prevProps.value && _inputRef.current === document.activeElement) {
            _wasChangedSinceBlur.current = true;
        }
    }, props);

    if (props.isSkeleton) {
        return <InputSkeleton {...props} />;
    }


    return props.isReadOnly && isObjectEmpty(props.auditTrailData)
        ? <ReadOnlyText {...props} />
        : <InputRenderer props={props} inputRef={_inputRef} wrapperRef={_wrapperRef} handlers={handlers} />;
});


const InputWithErrorAndTooltip = withDomManipulator(withErrorAndTooltip(Input));

// Input with string formatter - note: we need to export it separately so we don't stack more withFormatter HOC
export { Input as InputComponentType, InputWithErrorAndTooltip };

export default withFormatter<IInputProps & WithErrorAndTooltipProps, string>(InputWithErrorAndTooltip);

export const InputSkeleton: React.FunctionComponent<IInputProps> = (props) => {
    const borders: React.CSSProperties = {};
    const borderRadius = "17px";

    // by default Skeleton has correct radius for sharp corners
    // => set to special skeleton radius only if not set
    if (!props.isSharpLeft) {
        borders.borderTopLeftRadius = borderRadius;
        borders.borderBottomLeftRadius = borderRadius;
    }

    if (!props.isSharpRight) {
        borders.borderTopRightRadius = borderRadius;
        borders.borderBottomRightRadius = borderRadius;
    }

    return <Skeleton width={props.width ?? BasicInputSizes.M} height={"34px"} style={borders} />;
};
